"use client";

import styles from "@/styles/home/recommend-list.module.scss";
import { useState, useEffect, useRef, useCallback } from "react";
import ProductItem from "@/components/global/ProductItem";
import productApi from '@/service/modules/productApi'
import type { ProductType } from '@/types/product';


interface RecommendListProps {
  productId: string | number;
}
export default function ProductRecommendList({ productId }: RecommendListProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const hasRequestedRef = useRef(false)
  const [shouldLoadRecommend, setShouldLoadRecommend] = useState(false)
  const [productList, setProductList] = useState<ProductType[]>([])

  const getSimilar = useCallback(async () => {
    try {
      const res = await productApi.getSimilar(productId)

      if (res.data && res.data.pageList && res.data.pageList.items.length) { 
        setProductList(res.data.pageList.items as ProductType[])
      }
    } catch (error: unknown) {
      console.error(error)
    }
  }, [productId])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    const target = containerRef.current
    if (!target) {
      return
    }

    if (!("IntersectionObserver" in window)) {
      setShouldLoadRecommend(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting)
        if (isVisible) {
          setShouldLoadRecommend(true)
          observer.disconnect()
        }
      },
      {
        root: null,
        rootMargin: "200px 0px",
        threshold: 0.01,
      }
    )

    observer.observe(target)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!shouldLoadRecommend || hasRequestedRef.current) {
      return
    }
    hasRequestedRef.current = true
    getSimilar()
  }, [shouldLoadRecommend, getSimilar])

  return (
    <div
      className={`${styles["comp-recommend-list"]} ${styles["product-detail-recommend-list"]}`}
      ref={containerRef}
    >

      <div className={styles["recommend-title"]}>
        You Might Also Like
      </div>

      {
        productList.length > 0 && (
          <div className={styles["recommend-list"]}>
            {productList.map((product: ProductType) => (
              <div className={styles["recommend-item"]} key={product.productID}>
                <ProductItem product={product} />
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}
